import React, { createContext, useContext, useEffect, useState } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase } from '../lib/supabaseClient';

interface AuthContextValue {
    user: User | null;
    loading: boolean;
    isRecovery: boolean;
    signIn: (email: string, password: string) => Promise<string | null>;
    signOut: () => Promise<void>;
    requestPasswordReset: (email: string) => Promise<string | null>;
    updatePassword: (newPassword: string) => Promise<string | null>;
    clearRecovery: () => void;
}

const AuthContext = createContext<AuthContextValue>({
    user: null,
    loading: true,
    isRecovery: false,
    signIn: async () => null,
    signOut: async () => {},
    requestPasswordReset: async () => null,
    updatePassword: async () => null,
    clearRecovery: () => {},
});

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);
    const [isRecovery, setIsRecovery] = useState(false);

    useEffect(() => {
        supabase.auth.getSession().then(({ data: { session } }) => {
            setUser(session?.user ?? null);
            setLoading(false);
        });

        const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
            setUser(session?.user ?? null);
            if (event === 'PASSWORD_RECOVERY') {
                setIsRecovery(true);
            }
        });

        return () => subscription.unsubscribe();
    }, []);

    const signIn = async (email: string, password: string): Promise<string | null> => {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        return error ? error.message : null;
    };

    const signOut = async (): Promise<void> => {
        await supabase.auth.signOut();
    };

    const requestPasswordReset = async (email: string): Promise<string | null> => {
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
            redirectTo: `${window.location.origin}/reset-password`,
        });
        return error ? error.message : null;
    };

    const updatePassword = async (newPassword: string): Promise<string | null> => {
        // Note: don't clear recovery here — the ResetPassword component needs to
        // stay mounted to show its success state. clearRecovery() dismisses it.
        const { error } = await supabase.auth.updateUser({ password: newPassword });
        return error ? error.message : null;
    };

    const clearRecovery = (): void => {
        setIsRecovery(false);
        // Drop the /reset-password path so a refresh doesn't reopen the flow.
        if (window.location.pathname === '/reset-password') {
            window.history.replaceState({ page: 'home' }, '', '/');
        }
    };

    return (
        <AuthContext.Provider value={{ user, loading, isRecovery, signIn, signOut, requestPasswordReset, updatePassword, clearRecovery }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = (): AuthContextValue => useContext(AuthContext);
